/**
 * SopEmailDrawer — email one SOP, or several picked in the SOP Library.
 *
 * Approved by Chris 2026-10-13 (content, recipients and trigger are in
 * middleware/app/services/sop_email.py): the sender picks colleagues
 * (internal only), adds an optional note and clicks Send; one email goes out
 * from "SPI Ops" with each SOP attached as a PDF, CC and Reply-To the sender.
 */
import { useMemo, useState } from 'react'
import { X } from 'lucide-react'
import { Button, Drawer, Input, Textarea, toast } from '@/components/ui'
import { useEmailSops, useSopRecipients } from '../api/queries'

export interface SopEmailDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sops: { id: string; title: string }[]
  /** Called after a successful send (e.g. to clear a list selection). */
  onSent?: () => void
}

export const MAX_EMAIL_SOPS = 25

export function SopEmailDrawer({ open, onOpenChange, sops, onSent }: SopEmailDrawerProps) {
  const [search, setSearch] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [note, setNote] = useState('')
  const { data, isLoading } = useSopRecipients()
  const send = useEmailSops()

  const names = useMemo(() => new Map((data?.recipients ?? []).map((r) => [r.email, r.name || r.email])), [data])
  const candidates = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const all = data?.recipients ?? []
    return needle
      ? all.filter((r) => r.email.toLowerCase().includes(needle) || (r.name ?? '').toLowerCase().includes(needle))
      : all
  }, [data, search])

  const toggle = (email: string) =>
    setPicked((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]))

  const close = (o: boolean) => {
    if (!o) {
      setSearch('')
      setPicked([])
      setNote('')
      send.reset()
    }
    onOpenChange(o)
  }

  const handleSend = () =>
    send.mutate(
      { ids: sops.map((s) => s.id), recipients: picked, note: note.trim() || undefined },
      {
        onSuccess: (res) => {
          const people =
            res.recipients.length === 1 ? (names.get(res.recipients[0]!) ?? res.recipients[0]) : `${res.recipients.length} people`
          toast.success(`Sent to ${people}. You're copied.`)
          onSent?.()
          close(false)
        },
      },
    )

  const detail = (send.error as { detail?: unknown } | null)?.detail
  const errorText = send.error
    ? typeof detail === 'string'
      ? detail
      : 'The email could not be sent. Try again in a minute.'
    : null
  const tooMany = sops.length > MAX_EMAIL_SOPS
  const title = sops.length === 1 ? 'Email this SOP' : `Email ${sops.length} SOPs`

  return (
    <Drawer
      open={open}
      onOpenChange={close}
      title={title}
      size="standard"
      surface="form"
      footer={
        <>
          <Button variant="ghost" onClick={() => close(false)} disabled={send.isPending}>
            Cancel
          </Button>
          <span className="flex-1" />
          <Button
            variant="primary"
            onClick={handleSend}
            loading={send.isPending}
            disabled={picked.length === 0 || tooMany || sops.length === 0}
          >
            Send
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <section className="flex flex-col gap-1.5">
          <h3 className="text-md font-semibold text-primary">{sops.length === 1 ? 'SOP' : `SOPs (${sops.length})`}</h3>
          <ul className="flex max-h-36 flex-col gap-1 overflow-y-auto text-md text-secondary">
            {sops.map((s) => (
              <li key={s.id}>{s.title}</li>
            ))}
          </ul>
          {tooMany ? (
            <p role="alert" className="text-md text-danger-text">
              Email up to {MAX_EMAIL_SOPS} SOPs at a time. Untick some and try again.
            </p>
          ) : (
            <p className="text-sm text-secondary">Each SOP is attached as a PDF, with a link to it in SPI Ops.</p>
          )}
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-md font-semibold text-primary">To</h3>
          {picked.length ? (
            <div className="flex flex-wrap gap-1.5">
              {picked.map((email) => (
                <span
                  key={email}
                  className="inline-flex items-center gap-1 rounded-full border border-border-default bg-surface-sunk py-0.5 pl-2.5 pr-1 text-sm text-primary"
                >
                  {names.get(email) ?? email}
                  <button
                    type="button"
                    aria-label={`Remove ${names.get(email) ?? email}`}
                    onClick={() => toggle(email)}
                    className="inline-flex h-5 w-5 items-center justify-center rounded-full text-tertiary hover:bg-surface-hover hover:text-primary"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          ) : null}
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search colleagues by name or email"
            aria-label="Search colleagues"
          />
          {isLoading ? (
            <p className="text-sm text-secondary">Loading colleagues…</p>
          ) : candidates.length === 0 ? (
            <p className="text-sm text-secondary">No colleague matches.</p>
          ) : (
            <ul
              role="listbox"
              aria-label="Colleagues"
              aria-multiselectable="true"
              className="flex max-h-64 flex-col overflow-y-auto rounded-md border border-border-default bg-surface"
            >
              {candidates.map((r) => {
                const on = picked.includes(r.email)
                return (
                  <li key={r.email}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={on}
                      onClick={() => toggle(r.email)}
                      className={`flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-surface-hover ${on ? 'bg-accent-subtle' : ''}`}
                    >
                      <input type="checkbox" checked={on} readOnly tabIndex={-1} className="pointer-events-none" />
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate text-md text-primary">{r.name || r.email}</span>
                        {r.name ? <span className="truncate text-sm text-secondary">{r.email}</span> : null}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
          <p className="text-sm text-secondary">Only SPI colleagues can be emailed. You're copied on the email.</p>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-md font-semibold text-primary">Note (optional)</h3>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            aria-label="Note"
            placeholder="e.g. Please read before Monday's receiving shift."
          />
        </section>

        {errorText ? (
          <p role="alert" className="rounded-md border border-danger-border bg-danger-bg px-4 py-3 text-md text-danger-text">
            {errorText}
          </p>
        ) : null}
      </div>
    </Drawer>
  )
}
